import React, { useEffect } from 'react';
import { Form as BaseForm } from 'antd';
import { dot } from 'dot-object';
import { FieldData } from 'rc-field-form/es/interface';
import { ValidationError } from 'yup';
import cx from 'classnames';

import { useForm, useLang } from 'hooks';
import { Nullable } from 'types/common';
import { FormProps } from 'types/components';

import styles from './styles.module.css';

import ActionsItem from './ActionsItem';
import CheckboxItem from './CheckboxItem';
import Columns from './Columns';
import Item from './Item';
import UploadItem from './UploadItem';

const Form = <Values extends object>({
  className,
  form,
  validationSchema,
  layout = 'vertical',
  preserve = false,
  onValuesChange,
  onFinish,
  ...props
}: FormProps<Values>) => {
  const formInstance = useForm<Values>(form);
  const lang = useLang();

  const setFieldErrors = (name: string, errors?: string[]) => {
    const path: Array<string | number> = [];
    const fields: FieldData[] = [];

    // Generate an error for each part of the field name (in case of an array)
    // (e.g. user[0].name -> ['user'], ['user', 0], ['user', 0, 'name'])
    const parts = name
      .replace(/\[/g, '.')
      .replace(/]/g, '')
      .split('.')
      .map((part) => part.match(/^\d+$/) ? Number(part) : part);

    parts.forEach((part) => {
      path.push(part);

      fields.push({
        name: [...path],
        errors,
      });
    });

    formInstance.setFields(fields);
  };

  const validateValues = async (values: Values): Promise<Nullable<Values>> => {
    values = formInstance.prepareValues(values);

    if (!validationSchema) {
      return values;
    }

    // Clear the form errors
    Object.keys(dot(values)).forEach((name) => setFieldErrors(name));

    try {
      return await validationSchema.validate(values, { abortEarly: false });
    } catch (error) {
      if (!(error instanceof ValidationError)) {
        throw error;
      }

      // Display the form errors
      const fields: Record<string, string[]> = {};

      error.inner.forEach((error) => {
        const name = error.path as string;

        // Display only the first error
        if (!fields[name]) {
          fields[name] = error.errors;
        }
      });

      Object.entries(fields).forEach(([name, errors]) => setFieldErrors(name, errors));
    }

    return null;
  };

  const handleValuesChange = async (changedValues: Partial<Values>, values: Values) => {
    await validateValues(values);

    onValuesChange?.(changedValues, values);
  };

  const handleFinish = async (values: Values) => {
    const validatedValues = await validateValues(values);

    if (!validatedValues) {
      return;
    }

    if (!onFinish) {
      return;
    }

    const result = await onFinish(validatedValues);

    if (result !== false) {
      formInstance?.resetFields();
    }
  };

  useEffect(() => {
    // If the form contains errors, submit it again to trigger the validation
    // and update error message to match the locale
    if (formInstance.getFieldsError().some((field) => field.errors.length)) {
      formInstance.submit();
    }
  }, [formInstance, lang.locale]);

  return (
    <BaseForm
      className={cx(styles.root, className)}
      form={formInstance}
      layout={layout}
      preserve={preserve}
      onValuesChange={handleValuesChange}
      onFinish={handleFinish}
      {...props}
    />
  );
};

export default Object.assign(Form, {
  ActionsItem,
  CheckboxItem,
  Columns,
  Item,
  UploadItem,
});
